import { useNavigate } from 'react-router-dom'
import { Calendar } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import AssetEmptyState from '@/matter-asset-common/asset/AssetEmptyState'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { TimelineAssetActions } from '../store/slice'

interface TimelineAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no timeline yet. Thin wrapper over the shared
 * AssetEmptyState that supplies the timeline copy, illustration, CTA, and the
 * first-generation-error banner (shown after a failed Generate). Uses a timeline-specific
 * calendar mark so the blank state does not look like the medical chronology surface.
 */
export default function TimelineAssetEmptyState(props: TimelineAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  return (
    <AssetEmptyState
      illustration={
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-neutral-100 text-brand-neutral-700">
          <Calendar className="h-6 w-6" />
        </span>
      }
      title="Build your timeline"
      body="Build a complete timeline of the key events from your case files, with every event cited to its source."
      ctaLabel="Add Files"
      onCtaClick={() => navigate(matterPaths.timeline.addFiles(matterId))}
      notice={
        stagingTotal > 0
          ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
          : undefined
      }
      error={
        initializeError
          ? {
              headline: 'Timeline could not be generated.',
              detail: 'Please try again.',
              dismissLabel: 'Dismiss',
              onDismiss: () => dispatch(TimelineAssetActions.setInitializeError({ matterId, error: null })),
            }
          : undefined
      }
    />
  )
}
